import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, uploadFile } from '../api/client'
import type { User } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { fetchMicrosoftProfilePhoto } from '../auth/graphPhoto'
import { prepareProfilePhoto } from '../lib/imageResize'
import { forgetPhoto } from '../lib/photos'
import { ImageCropper } from './ImageCropper'
import { Avatar, Card } from './ui'

/**
 * Profilbillede: upload fra enheden (kamera på mobil) eller hent fra Microsoft 365.
 * Billedet skaleres til 512x512 JPEG i browseren, før det sendes til API-et.
 */
export function ProfilePhotoCard({ user }: { user: User }) {
  const { refreshProfile } = useAuth()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  /** Valgt fil der venter på at brugeren har valgt udsnit i beskæringsværktøjet. */
  const [pendingFile, setPendingFile] = useState<Blob | null>(null)

  async function afterChange(updated: User) {
    forgetPhoto(user.photoUrl)
    await refreshProfile()
    await queryClient.invalidateQueries({ queryKey: ['users'] })
    return updated
  }

  /** Sender et billede der allerede er beskåret/skaleret klar til upload. */
  async function upload(blob: Blob, source?: 'microsoft365') {
    const query = source ? `?source=${source}` : ''
    const updated = await uploadFile<User>(`/api/users/${user.id}/photo${query}`, blob, 'profile.jpg')
    await afterChange(updated)
  }

  /** Filen vises først i beskæringsværktøjet - brugeren vælger selv udsnittet. */
  function onFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setMessage(null)
    setPendingFile(file)
  }

  async function onCropped(blob: Blob) {
    setBusy('upload')
    try {
      await upload(blob)
      setPendingFile(null)
      setMessage({ ok: true, text: 'Profilbilledet er opdateret.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Billedet kunne ikke gemmes.' })
    } finally {
      setBusy(null)
    }
  }

  async function importFromMicrosoft() {
    setMessage(null)
    setBusy('microsoft')
    try {
      const blob = await fetchMicrosoftProfilePhoto()
      if (!blob) {
        setMessage({ ok: false, text: 'Der blev ikke fundet et profilbillede på din Microsoft 365-konto.' })
        return
      }
      // Billedet fra Microsoft 365 er allerede beskåret af brugeren i Microsoft - det skaleres blot.
      await upload(await prepareProfilePhoto(blob), 'microsoft365')
      setMessage({ ok: true, text: 'Profilbilledet er hentet fra Microsoft 365.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Billedet kunne ikke hentes.' })
    } finally {
      setBusy(null)
    }
  }

  async function removePhoto() {
    setMessage(null)
    setBusy('remove')
    try {
      const updated = await api.delete<User>(`/api/users/${user.id}/photo`)
      await afterChange(updated)
      setMessage({ ok: true, text: 'Profilbilledet er fjernet.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Billedet kunne ikke fjernes.' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card title="Profilbillede">
      <div className="profile-photo">
        <Avatar user={user} size={84} />

        <div className="profile-photo__actions">
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={onFileSelected}
          />
          <button className="btn btn--sm btn--primary" disabled={busy !== null} onClick={() => fileInput.current?.click()}>
            {busy === 'upload' ? 'Uploader ...' : 'Vælg billede'}
          </button>

          {user.authProvider === 'entra' && (
            <button className="btn btn--sm" disabled={busy !== null} onClick={() => void importFromMicrosoft()}>
              {busy === 'microsoft' ? 'Henter ...' : 'Hent fra Microsoft 365'}
            </button>
          )}

          {user.photoUrl && (
            <button className="btn btn--sm btn--danger" disabled={busy !== null} onClick={() => void removePhoto()}>
              Fjern
            </button>
          )}
        </div>

        <div className="profile-photo__hint">
          Du vælger selv udsnittet efter at have valgt en fil. Billedet gemmes som 512x512. JPEG, PNG eller WebP.
        </div>
      </div>

      {pendingFile && (
        <ImageCropper
          file={pendingFile}
          busy={busy === 'upload'}
          onCancel={() => setPendingFile(null)}
          onCrop={onCropped}
        />
      )}

      {message && (
        <div
          className={message.ok ? 'small' : 'error-box'}
          style={{ marginTop: 12, color: message.ok ? 'var(--success)' : undefined }}
        >
          {message.text}
        </div>
      )}
    </Card>
  )
}
